import { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { Box } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import duskTheme from './theme/duskTheme.jsx';
import Header from './components/Header.js';
import Footer from './components/Footer';
import { ToastProvider } from './context/ToastContext.jsx';
import ToastContainer from './components/ToastContainer.jsx';
import { User } from "./models/User";
import { QuestionsProvider } from './context/QuestionsContext';
import ProtectedRoute from './components/ProtectedRoute';
import { useQueryClient } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';

// Import your page components
import Home from './features/home/pages/Home.js';
import Quiz from './features/quiz/pages/Quiz.jsx';
import Admin from './features/admin/pages/Admin.jsx';
import Auth from './features/auth/pages/Auth';
import Leaderboard from './features/leaderboard/pages/Leaderboard';
import ProfilePage from './features/profiles/pages/UserProfile2.jsx';
import ForgotPassword from './features/auth/pages/ForgotPassword';
import ResetPassword from './features/auth/pages/ResetPassword';
import VerifyEmail from './features/auth/pages/VerifyEmail';
import SharedResultsPage from './features/quiz/pages/SharedResultsPage';
import QuestionBreakdownPage from './features/stats/pages/QuestionBreakdownPage';
import About from './pages/About';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsConditions from './pages/TermsConditions';
import Contact from './pages/Contact';
import QuestionDifficultyPage from './pages/QuestionDifficultyPage';
import ScrollToTop from './components/ScrollToTop';
import TeamPage from './features/teams/pages/TeamPage';


// Wrapper component to sync localStorage on route changes
function AppContent() {
  
  const [user, setUser] = useState<User | null>(null);
  const [showLogin, setShowLogin] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const isSnapshotting = typeof navigator !== 'undefined' && navigator.userAgent.includes('ReactSnap');
  const location = useLocation();
  console.log('🏠 AppContent rendering', { userId: user?.userId, showLogin });

  // Load user from localStorage on first render
  useEffect(() => {
    const loadUser = () => {
      const storedUser = localStorage.getItem('triviaUser');
      if (storedUser) {
        try {
          const parsed = JSON.parse(storedUser);
          setUser(parsed);
          console.log('📥 Loaded user from localStorage:', parsed);
        } catch (error) {
          console.error('Failed to parse stored user:', error);
          localStorage.removeItem('triviaUser');
        }
      }
    };

    loadUser();
  }, []);

  const queryClient = useQueryClient();

  // Mirrors Home.js's isHydrated pattern. isSnapshotting check is required: without it, this effect fires during react-snap's 
  // Puppeteer pass too, flipping isHydrated to true *in the snapshot itself* (no user in localStorage during snap → 
  // Auth modal gets baked into the static HTML). Real users' first paint always starts isHydrated=false, so that mismatched
  // against the snapshot and caused 418. Skipping the flip during the snap keeps both sides starting from the same false state.
  useEffect(() => {
    if (!isSnapshotting) {
      setIsHydrated(true);
    }
  }, []);

  useEffect(() => {
  if (location.pathname === '/leaderboard') {
    const timer = setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    }, 500);
    return () => clearTimeout(timer);
  }

  if (location.pathname === '/profile' && user?.userId) {
    const timer = setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: ['user', user.userId] });
    }, 500);
    return () => clearTimeout(timer);
  }
}, [location.pathname, user?.userId]);

  useEffect(() => {
    console.log('🔄 Route changed to:', location.pathname);
  }, [location.pathname]);

  // Show login modal ONLY on initial mount if no user exists
  // BUT not on password reset pages
  useEffect(() => {
  const isPasswordResetPage = 
    location.pathname === '/forgot-password' || 
    location.pathname === '/reset-password' ||
    location.pathname === '/verify-email';

  // Team pages handle their own auth prompt — don't auto-show modal
  const isTeamPage = location.pathname.startsWith('/teams/');

  //TODO: Improve this long term
  if (!user && !isPasswordResetPage && !isTeamPage) {
    setShowLogin(true);
  } else {
    setShowLogin(false);
  }
  }, [user?.userId, location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem('triviaUser');
    setUser(null);
    setShowLogin(true);
  };

  const handleSetUser = (userData: Partial<User>) => {
    const fullUser: User = {
      userId: userData.userId || "",
      email: userData.email || "example@example.com",
      passwordHash: userData.passwordHash || "",
      username: userData.username || "Unknown",
      userRole: userData.userRole || "user",
      totalQuestionsAnswered: userData.totalQuestionsAnswered ?? 0,
      totalCorrectAnswers: userData.totalCorrectAnswers ?? 0,
      team: userData.team ?? null,
      avatarUrl: userData.avatarUrl ?? null,
      createdAt: userData.createdAt || new Date().toISOString(),
      updatedAt: userData.updatedAt || new Date().toISOString(),
      weeklyCorrect: userData.weeklyCorrect ?? 0,
      monthlyCorrect: userData.monthlyCorrect ?? 0,
      weeklyPeriod: userData.weeklyPeriod ?? null,
      monthlyPeriod: userData.monthlyPeriod ?? null,
    };

    setUser(fullUser);
    localStorage.setItem("triviaUser", JSON.stringify(fullUser));
    setShowLogin(false);
    console.log("✅ Logged In User:", fullUser);
  };

  const handleUpdateUser = useCallback((updatedUser: User) => {
    console.log('🔄 handleUpdateUser updatedUser :', { updatedUser });
    setUser(updatedUser);
    localStorage.setItem("triviaUser", JSON.stringify(updatedUser));
  }, []);

  return (
    <Box minHeight="100vh" bgcolor="background.default" display="flex" flexDirection="column">
      <ScrollToTop />
      {/* Header visible on all pages */}
      <Header
        user={user}
        onLogout={handleLogout}
        onShowLogin={() => setShowLogin(true)}
      />

      <ToastContainer />

      {/* Login Modal - accessible from any page */}
      {isHydrated && !user && (
        <Auth
          isOpen={showLogin}
          onSetUser={handleSetUser}
          onClose={() => {
            setShowLogin(false);
            window.scrollTo(0, 0);
          }}
        />
      )}

      {/* Routes */}
      <Box component="main" flex={1}>
        <Routes>
          <Route path="/" element={<Home user={user} onUpdateUser={handleUpdateUser} />} />
          {/* @ts-ignore */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute user={user} requiredRole="admin">
                <Admin user={user} />
              </ProtectedRoute>
            }
          />
          {/* @ts-ignore */}
          <Route path="/quiz" element={<Quiz user={user} onUpdateUser={handleUpdateUser} />} />
          <Route
            path="/results"
            element={
              <SharedResultsPage
                user={user}
                onShowLogin={() => setShowLogin(true)}
              />
            }
          />
          {/* @ts-ignore */}
          <Route path="/leaderboard" element={<Leaderboard user={user} />} />
          {/* @ts-ignore */}
          <Route
            path="/profile"
            element={
              <ProfilePage
                user={user}
                onUpdateUser={handleUpdateUser}
              />
            }
          />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/question-breakdown" element={<QuestionBreakdownPage />} />
          <Route path="/question-difficulty" element={<QuestionDifficultyPage />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsConditions />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/about" element={<About />} />
          <Route
            path="/teams/griffins"
            element={
              <TeamPage
                teamKey="Griffins"
                user={user}
                onUpdateUser={handleUpdateUser}
                onShowLogin={() => setShowLogin(true)}
                meta={{
                  title: "The Griffins | The Quizzler",
                  description: "Everything you need to know about the Griffins — one of four rival teams competing for glory on The Quizzler Harry Potter trivia site.",
                  canonical: "https://thequizzler.app/teams/griffins",
                }}
              />
            }
          />
          <Route
            path="/teams/snakes"
            element={
              <TeamPage
                teamKey="Snakes"
                user={user}
                onUpdateUser={handleUpdateUser}
                onShowLogin={() => setShowLogin(true)}
                meta={{
                  title: "The Snakes | The Quizzler",
                  description: "Meet the Snakes — cunning, ambitious, and always playing to win. Join or learn about this rival team on The Quizzler.",
                  canonical: "https://thequizzler.app/teams/snakes",
                }}
              />
            }
          />
          <Route
            path="/teams/badgers"
            element={
              <TeamPage
                teamKey="Badgers"
                user={user}
                onUpdateUser={handleUpdateUser}
                onShowLogin={() => setShowLogin(true)}
                meta={{
                  title: "The Badgers | The Quizzler",
                  description: "The Badgers value loyalty, patience, and hard work. Discover this team's identity and join the rivalry on The Quizzler.",
                  canonical: "https://thequizzler.app/teams/badgers",
                }}
              />
            }
          />
          <Route
            path="/teams/eagles"
            element={
              <TeamPage
                teamKey="Eagles"
                user={user}
                onUpdateUser={handleUpdateUser}
                onShowLogin={() => setShowLogin(true)}
                meta={{
                title: "The Eagles | The Quizzler",
                description: "The Eagles prize wisdom and wit above all. Learn about this team and compete for trivia glory on The Quizzler.",
                canonical: "https://thequizzler.app/teams/eagles",
              }}
              />
            }
          />
        </Routes>
      </Box>

      <Footer />
    </Box>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <ThemeProvider theme={duskTheme}>
        <CssBaseline />
        <QuestionsProvider>
          <ToastProvider>
            <BrowserRouter>
              <AppContent />
            </BrowserRouter>
          </ToastProvider>
        </QuestionsProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}